import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Search } from "lucide-react";

export default function HelpKbSearch() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(q);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    api.get("/kb/articles", { params: { q, limit: 50 } })
      .then(({ data }) => { setResults(data.items || []); setTotal(data.total || 0); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <AppShell title="Search">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <Link to="/help/kb" className="inline-flex items-center text-sm text-navy-500 hover:text-navy-900 mb-4" data-testid="kb-search-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Knowledge base
        </Link>
        <form
          onSubmit={(e) => { e.preventDefault(); if (draft.trim().length >= 2) setParams({ q: draft.trim() }); }}
          className="flex mb-6"
        >
          <div className="flex-1 flex items-center gap-2 bg-white dark:bg-navy-900 border border-navy-200 rounded-l-lg px-3 focus-within:ring-2 focus-within:ring-orange-300">
            <Search className="w-4 h-4 text-navy-400" />
            <input
              type="search"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 py-2 bg-transparent outline-none text-sm"
              data-testid="kb-search-page-input"
            />
          </div>
          <button type="submit" className="px-5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-r-lg">Search</button>
        </form>
        <div className="text-xs text-navy-500 mb-3" data-testid="kb-search-summary">
          {loading ? "Searching…" : `${total} result${total === 1 ? "" : "s"} for “${q}”`}
        </div>
        <div className="space-y-2" data-testid="kb-search-results">
          {results.map((a) => (
            <Link
              key={a.id}
              to={`/help/kb/${a.slug}`}
              className="eb-card p-4 block hover:border-orange-300 transition-colors"
              data-testid={`kb-search-result-${a.slug}`}
            >
              <div className="font-semibold text-navy-900 dark:text-white">{a.title}</div>
              {a.snippet && (
                <div
                  className="text-xs text-navy-500 mt-1"
                  dangerouslySetInnerHTML={{
                    __html: highlight(a.snippet, q),
                  }}
                />
              )}
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function highlight(snippet, term) {
  if (!term) return snippet;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return snippet.replace(new RegExp(`(${escaped})`, "gi"), '<mark class="bg-orange-100 text-orange-800 rounded px-0.5">$1</mark>');
}
